// apps/spa/src/components/commands/CommandTable.tsx
// CMD-01 — shadcn Table for the /commands list view.
//
// Columns: Name · Description · Type · Status · Enabled · Actions
//   Name        — NavLink to /commands/:id/edit with leading slash (e.g. /charter)
//   Description — truncated at 60 chars; full text in title= attribute
//   Type        — icon+label cell (📄 Static / 📊 Dynamic / 👥 Member / 🔒 Admin)
//   Status      — CommandStatusBadge derived from registeredAt/lastRegisterError/enabled
//   Enabled     — shadcn Switch wired to useToggleCommand (optimistic update)
//   Actions     — DropdownMenu via CommandRowActions (Edit / Retry registration / Delete)
//
// Default sort: updatedAt DESC (matches reminder list convention).
//
// Phase 7 (Plan 07-05): AnimatePresence + motion.tr row enter/exit + Type column.

import { useMemo } from 'react';
import { Link } from 'react-router';
import { motion, AnimatePresence } from 'motion/react';
import type { SlashCommand } from '@guardian/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useToggleCommand } from '@/hooks/useCommands';
import { CommandStatusBadge } from './CommandStatusBadge';
import { CommandRowActions } from './CommandRowActions';

const MAX_DESC_CHARS = 60;

function truncate(s: string): string {
  return s.length > MAX_DESC_CHARS ? `${s.slice(0, MAX_DESC_CHARS)}…` : s;
}

// ---------------------------------------------------------------------------
// Type column — LOCKED icons per 07-CONTEXT.md
// ---------------------------------------------------------------------------

const TYPE_DISPLAY: Record<string, { icon: string; label: string }> = {
  static:  { icon: '📄', label: 'Static' },
  dynamic: { icon: '📊', label: 'Dynamic' },
  utility: { icon: '👥', label: 'Member' },
  admin:   { icon: '🔒', label: 'Admin' },
};

function CommandTypeCell({ type }: { type: string }) {
  const d = TYPE_DISPLAY[type] ?? TYPE_DISPLAY.static;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span aria-hidden="true">{d.icon}</span>
      <span>{d.label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Animation config
// ---------------------------------------------------------------------------

const rowVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96 },
};

const rowTransition = { type: 'spring' as const, stiffness: 300, damping: 25 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CommandTableProps {
  commands: SlashCommand[];
  onDelete: (id: string) => void;
}

export function CommandTable({ commands, onDelete }: CommandTableProps) {
  const toggle = useToggleCommand();

  const sorted = useMemo(
    () => [...commands].sort((a, b) => b.updatedAt - a.updatedAt),
    [commands],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Enabled</TableHead>
          <TableHead aria-label="Row actions" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <AnimatePresence mode="popLayout" initial={false}>
          {sorted.map((cmd) => {
            const disabled = !cmd.enabled;
            const shortDesc = truncate(cmd.description);

            return (
              <motion.tr
                key={cmd.id}
                layout
                variants={rowVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={rowTransition}
                data-disabled={disabled ? 'true' : undefined}
                className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
              >
                <TableCell>
                  <Link
                    to={`/commands/${cmd.id}/edit`}
                    className={cn(
                      'font-medium hover:underline font-mono',
                      disabled && 'text-muted-foreground',
                    )}
                  >
                    /{cmd.name}
                  </Link>
                </TableCell>
                <TableCell
                  title={cmd.description}
                  className={cn('text-sm', disabled && 'text-muted-foreground')}
                >
                  {shortDesc}
                </TableCell>
                <TableCell>
                  <CommandTypeCell type={cmd.type ?? 'static'} />
                </TableCell>
                <TableCell>
                  <CommandStatusBadge command={cmd} />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={cmd.enabled}
                    onCheckedChange={(checked) =>
                      toggle.mutate({ id: cmd.id, enabled: checked })
                    }
                    aria-label={`Toggle /${cmd.name}`}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <CommandRowActions command={cmd} onDelete={onDelete} />
                </TableCell>
              </motion.tr>
            );
          })}
        </AnimatePresence>
      </TableBody>
    </Table>
  );
}
